import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import mascot from "@/assets/mascot.png";
import { useLang } from "@/hooks/use-language";
import { listPublicValues, type ValueRow } from "@/lib/values.functions";

export const Route = createFileRoute("/values")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/values" },
      { title: "Values & Character | القيم — جذوري" },
      {
        name: "description",
        content:
          "تصفّح القيم على «جذوري» — قصص ولحظات حانية حول الصدق، الاحترام، الصبر، التعاون، وأكثر. Browse Jozori values: honesty, respect, patience, gratitude and more.",
      },
      { property: "og:title", content: "Values & Character — Jozori | جذوري" },
      {
        property: "og:description",
        content:
          "Stories and gentle moments organized by the value they nurture — honesty, respect, patience, gratitude and more.",
      },

      { rel: "canonical", href: "https://jozori.com/values" } as never,
    ],
  }),
  loader: () => listPublicValues(),
  component: ValuesRouteShell,
});

function ValuesRouteShell() {
  const { pathname } = useLocation();
  return pathname === "/values" ? <ValuesIndex /> : <Outlet />;
}

function ValuesIndex() {
  const values = Route.useLoaderData() as ValueRow[];
  const { lang, dir, toggle } = useLang();
  const isAr = lang === "ar";

  return (
    <div dir={dir} className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-clay/30 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2 font-display-en text-lg font-black">
            <img src={mascot} alt="" className="size-8 rounded-full ring-2 ring-primary/30" />
            <span>Jozori</span>
          </Link>
          <button
            onClick={toggle}
            className="rounded-full border border-clay/40 bg-card px-3 py-1.5 text-xs font-bold hover:bg-clay/20"
          >
            {isAr ? "English" : "العربية"}
          </button>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-br from-primary/15 via-accent/10 to-secondary/15"
        />
        <div className="relative mx-auto max-w-4xl px-4 py-16 text-center">
          <p className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-primary">
            {isAr ? "القيم" : "Values & character"}
          </p>
          <h1
            className={`mx-auto max-w-3xl text-4xl md:text-5xl font-black leading-tight ${
              isAr ? "font-display-ar" : "font-display-en"
            }`}
          >
            {isAr ? "تصفّح حسب القيمة التي تودّ غرسها" : "Browse by the value you want to nurture"}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-foreground/70">
            {isAr
              ? "كل قصة، وكل لحظة هادئة على «جذوري» — مبنيّة حول قيمة واضحة تساعد طفلك على النمو بثقة وانتماء."
              : "Every story and gentle moment on Jozori is built around a clear value — to help your child grow with confidence and belonging."}
          </p>

        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12">
        {values.length === 0 ? (
          <p className="text-center text-foreground/60">
            {isAr ? "لا توجد قيم بعد." : "No values defined yet."}
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {values.map((v) => (
              <Link
                key={v.code}
                to="/values/$code"
                params={{ code: v.code }}
                className="group rounded-3xl border-2 border-clay/30 bg-card p-6 hover:border-primary hover:-translate-y-1 transition-all"
              >
                <div className="text-5xl mb-3">{v.emoji}</div>
                <h2
                  className={`text-xl font-black ${
                    isAr ? "font-display-ar" : "font-display-en"
                  }`}
                >
                  {isAr ? v.label_ar : v.label_en}
                </h2>
                {(isAr ? v.description_ar : v.description_en) && (
                  <p className="mt-1 text-sm text-foreground/70 line-clamp-2">
                    {isAr ? v.description_ar : v.description_en}
                  </p>
                )}
                <span className="mt-3 inline-block text-xs font-bold text-primary">
                  {isAr ? "استكشف ←" : "Explore →"}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
